import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { ToastProvider } from './contexts/ToastContext';
import { DeployTrackerProvider } from './contexts/DeployTrackerContext';
import { ToastContainer } from './components/toast/ToastContainer';
import { DeployTrackerWidget } from './components/app/DeployTrackerWidget';
import { Layout } from './components/Layout';
import { SectionLayout, type SectionTab } from './components/AdminLayout';
import { AdminGuard } from './components/AdminGuard';
import { AppStore } from './pages/AppStore';
import { AccountRequests } from './pages/AccountRequests';
import { UserManagement } from './pages/admin/UserManagement';
import { AppManagement } from './pages/admin/AppManagement';
import { OrganizationManagement } from './pages/admin/OrganizationManagement';
import AppDetail from './pages/admin/AppDetail';
import AppOverview from './pages/admin/AppOverview';
import AppAccess from './pages/admin/AppAccess';
import AppSettings from './pages/admin/AppSettings';
import InstanceManagement from './pages/admin/InstanceManagement';
import SettingsPage from './pages/admin/SettingsPage';
import { TemplateManagement } from './pages/admin/TemplateManagement';
import { TakAdmin } from './pages/admin/TakAdmin';
import AppDeployChat from './pages/admin/AppDeployChat';
import AppDeployForm from './pages/admin/AppDeployForm';
import DeployedApps from './pages/admin/DeployedApps';
import DeployedAppDetail from './pages/admin/DeployedAppDetail';
import AppAuditLog from './pages/admin/AppAuditLog';

/** Secondary-nav tabs for each top-level section. Home tabs use `end` so they
 *  are only active on the exact section root, not its sub-routes. */
const APPS_TABS: SectionTab[] = [
  { label: 'App Management', path: '/apps', end: true },
  { label: 'App Audit', path: '/apps/audit' },
];
const USERS_TABS: SectionTab[] = [
  { label: 'User Management', path: '/users', end: true },
  { label: 'Account Requests', path: '/users/requests' },
  { label: 'Organizations', path: '/users/organizations' },
  { label: 'Templates', path: '/users/templates' },
];
const ADMIN_TABS: SectionTab[] = [
  { label: 'Settings', path: '/admin', end: true },
  { label: 'TAK Server', path: '/admin/tak' },
];

/** Root app with routing + global providers (toasts, deploy tracker). */
export function App(): React.JSX.Element {
  return (
    <ToastProvider>
      <DeployTrackerProvider>
        <BrowserRouter>
          <AppRoutes />
          <DeployTrackerWidget />
          <ToastContainer />
        </BrowserRouter>
      </DeployTrackerProvider>
    </ToastProvider>
  );
}

/** Nested so we can keep the provider tree above outside the router. */
function AppRoutes(): React.JSX.Element {
  return (
    <Routes>
      <Route element={<Layout />}>
        {/* Home — the app launcher, visible to everyone. */}
        <Route index element={<AppStore />} />

        {/* Apps — App Management (home) + App Audit, plus deploy flows and
            per-app detail. Admin-only. */}
        <Route path="apps" element={<AdminGuard><SectionLayout tabs={APPS_TABS} /></AdminGuard>}>
          <Route index element={<AppManagement />} />
          <Route path="audit" element={<AppAuditLog />} />
          <Route path="deploy" element={<AppDeployForm />} />
          <Route path="deploy-chat" element={<AppDeployChat />} />
          <Route path="deployed" element={<DeployedApps />} />
          <Route path="deployed/:slug" element={<DeployedAppDetail />} />
          <Route path=":appId" element={<AppDetail />}>
            <Route index element={<AppOverview />} />
            <Route path="access" element={<AppAccess />} />
            <Route path="instances" element={<InstanceManagement />} />
            <Route path="settings" element={<AppSettings />} />
          </Route>
        </Route>

        {/* User Management (home) + Account Requests, Organizations, Templates. Admin-only. */}
        <Route path="users" element={<AdminGuard><SectionLayout tabs={USERS_TABS} /></AdminGuard>}>
          <Route index element={<UserManagement />} />
          <Route path="requests" element={<AccountRequests />} />
          <Route path="organizations" element={<OrganizationManagement />} />
          <Route path="templates" element={<TemplateManagement />} />
        </Route>

        {/* Admin — Settings (home) + TAK Server. Admin-only. */}
        <Route path="admin" element={<AdminGuard><SectionLayout tabs={ADMIN_TABS} /></AdminGuard>}>
          <Route index element={<SettingsPage />} />
          <Route path="tak" element={<TakAdmin />} />
        </Route>

        {/* Redirects from the old structure so existing links/bookmarks keep working. */}
        <Route path="store" element={<Navigate to="/" replace />} />
        <Route path="requests" element={<Navigate to="/users/requests" replace />} />
        <Route path="admin/users" element={<Navigate to="/users" replace />} />
        <Route path="admin/requests" element={<Navigate to="/users/requests" replace />} />
        <Route path="admin/organizations" element={<Navigate to="/users/organizations" replace />} />
        <Route path="admin/templates" element={<Navigate to="/users/templates" replace />} />
        <Route path="admin/settings" element={<Navigate to="/admin" replace />} />
        <Route path="admin/app-audit" element={<Navigate to="/apps/audit" replace />} />
        <Route path="admin/deploy" element={<Navigate to="/apps/deploy" replace />} />
        <Route path="admin/deploy-chat" element={<Navigate to="/apps/deploy-chat" replace />} />
        <Route path="admin/deployed" element={<Navigate to="/apps/deployed" replace />} />
        <Route path="admin/deployed/:slug" element={<LegacyDeployedRedirect />} />
        <Route path="admin/apps" element={<Navigate to="/apps" replace />} />
        <Route path="admin/apps/:appId/*" element={<LegacyAppDetailRedirect />} />

        {/* Older redirect aliases carried over from the previous structure. */}
        <Route path="modules" element={<Navigate to="/apps" replace />} />
        <Route path="security" element={<Navigate to="/admin" replace />} />
      </Route>
    </Routes>
  );
}

/** Preserve the slug when redirecting a legacy /admin/deployed/:slug link. */
function LegacyDeployedRedirect(): React.JSX.Element {
  const { slug } = useParams();
  return <Navigate to={`/apps/deployed/${slug ?? ''}`} replace />;
}

/** Preserve the app id and any sub-tab when redirecting a legacy
 *  /admin/apps/:appId(/access|/instances|/settings) link. */
function LegacyAppDetailRedirect(): React.JSX.Element {
  const params = useParams();
  const appId = params.appId ?? '';
  const rest = params['*'];
  return <Navigate to={`/apps/${appId}${rest ? `/${rest}` : ''}`} replace />;
}
